import { RawModal } from '@karnov/kui-react'
import { AnimatePresence } from 'motion/react'
import { useCallback, useEffect, useRef, useState, type FC } from 'react'

import { UserCarouselSlug } from 'api/userCarousels/types'
import { useCountry } from 'hooks/useCountry'
import { useTranslation } from 'services/i18n'

import { AnimationWrapper } from './AnimationWrapper'
import { DismissCarouselButton } from './DismissCarouselButton'
import { DotNavigation } from './DotNavigation'
import { NavigationArrow } from './NavigationArrow'
import { ReadMoreButton } from './ReadMoreButton'
import { ScreenGraphics } from './ScreenGraphics'
import { ScreenText } from './ScreenText'
import { carouselConfigs } from './carouselConfigs'
import { SHOW_NEXT_SCREEN_INTERVAL } from './consts'
import { NavigationDirection } from './types'

type Props = {
  carouselSlug: UserCarouselSlug
  onDismissCarousel: VoidFunction
}

export const UserCarousel: FC<Props> = ({
  carouselSlug,
  onDismissCarousel,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'user_carousels',
  })

  const autoshowNextScreenIntervalRef = useRef<
    NodeJS.Timeout | string | number | undefined
  >(undefined)

  const [currentScreenIndex, setCurrentScreenIndex] = useState(0)

  const carouselConfig = carouselConfigs[useCountry()][carouselSlug]

  const screenCount = carouselConfig?.screens.length

  const currentScreen = carouselConfig?.screens[currentScreenIndex]

  const showNextScreen = useCallback(() => {
    setCurrentScreenIndex(prevState =>
      prevState < screenCount - 1 ? prevState + 1 : 0,
    )
  }, [screenCount])

  const showPreviousScreen = useCallback(() => {
    setCurrentScreenIndex(prevState =>
      prevState > 0 ? prevState - 1 : screenCount - 1,
    )
  }, [screenCount])

  const resetAutoshowNextScreenInterval = useCallback(() => {
    clearInterval(autoshowNextScreenIntervalRef.current)
    autoshowNextScreenIntervalRef.current = setInterval(
      showNextScreen,
      SHOW_NEXT_SCREEN_INTERVAL,
    )
  }, [showNextScreen])

  const handleNavigationArrowClick = useCallback(
    (navigationDirection: NavigationDirection) => {
      resetAutoshowNextScreenInterval()
      if (navigationDirection === 'next') {
        showNextScreen()
      }
      else {
        showPreviousScreen()
      }
    },
    [resetAutoshowNextScreenInterval, showNextScreen, showPreviousScreen],
  )

  const handleNavigationDotClick = useCallback(
    (index: number) => {
      resetAutoshowNextScreenInterval()
      setCurrentScreenIndex(index)
    },
    [resetAutoshowNextScreenInterval],
  )

  // Fix issue with content not being scrollable after closing the modal
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    autoshowNextScreenIntervalRef.current = setInterval(
      showNextScreen,
      SHOW_NEXT_SCREEN_INTERVAL,
    )

    return () => {
      clearInterval(autoshowNextScreenIntervalRef.current)
    }
  }, [showNextScreen])

  return (
    <RawModal
      isOpen
      name="user_carousel"
      onClose={onDismissCarousel}
      showCloseButton
    >
      <AnimatePresence>
        <div className="flex flex-col gap-8 px-2 py-8 sm:p-8">
          <div className="flex w-full flex-col gap-x-2 gap-y-3">
            {carouselConfig.hasTitle && (
              <div className="text-center font-display text-4xl text-default lg:text-5xl">
                {t(`carousels.${carouselSlug}.title`)}
              </div>
            )}
            <div className="flex justify-center">
              <AnimationWrapper currentScreenIndex={currentScreenIndex}>
                <ScreenGraphics screen={currentScreen} />
              </AnimationWrapper>
            </div>
            <div className="flex min-h-32 items-center gap-x-2 gap-y-8 lg:gap-8">
              <NavigationArrow
                navigationDirection="previous"
                onClick={handleNavigationArrowClick}
              />
              <div className="flex w-full max-w-3xl flex-col gap-2">
                <AnimationWrapper currentScreenIndex={currentScreenIndex}>
                  <ScreenText
                    carouselSlug={carouselSlug}
                    currentScreen={currentScreen}
                  />
                </AnimationWrapper>
              </div>
              <NavigationArrow
                navigationDirection="next"
                onClick={handleNavigationArrowClick}
              />
            </div>
            <DotNavigation
              carouselConfig={carouselConfig}
              currentScreenIndex={currentScreenIndex}
              onDotClick={handleNavigationDotClick}
            />
          </div>
          <div className="flex flex-col items-center gap-4">
            <DismissCarouselButton onClick={onDismissCarousel} />
            {carouselConfig.hasReadMoreButton && (
              <ReadMoreButton carouselSlug={carouselSlug} />
            )}
          </div>
        </div>
      </AnimatePresence>
    </RawModal>
  )
}
